import React, { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/Api";

export const AuthContext = createContext();

const ROLE_HIERARCHY = {
    ROLE_GOD: ["ROLE_SUPERADMIN", "ROLE_ADMIN", "ROLE_SEO", "ROLE_USER"],
    ROLE_SUPERADMIN: ["ROLE_ADMIN", "ROLE_SEO", "ROLE_USER"],
    ROLE_ADMIN: ["ROLE_USER", "ROLE_SEO"],
    ROLE_SEO: ["ROLE_USER"],
    ROLE_USER: [],
};

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState({
        isAuthenticated: !!localStorage.getItem("token"),
        user: null,
    });

    const [referer, setReferer] = useState("/");
    const [loading, setLoading] = useState(true);
    const [badges, setBadges] = useState({ auctions: 0, favorites: 0 });
    const navigate = useNavigate();

    useEffect(() => {
        const verifySession = async () => {
            const token = localStorage.getItem("token");
            if (token) {
                try {
                    const data = await api.checkSession();
                    setAuthState({
                        isAuthenticated: true,
                        user: data.user,
                    });
                } catch (error) {
                    console.error("Session verification failed", error);
                    localStorage.removeItem("token");
                    setAuthState({ isAuthenticated: false, user: null });
                }
            }
            setLoading(false);
        };

        verifySession();
    }, []);

    // Centralized badge polling
    useEffect(() => {
        let intervalId;
        const fetchBadges = async () => {
            try {
                const resp = await api.getBadges();
                const auctions = Number(resp?.auctions ?? 0);
                const favorites = Number(resp?.favorites ?? 0);
                if (!Number.isFinite(auctions) || !Number.isFinite(favorites)) return;
                setBadges((prev) =>
                    prev.auctions !== auctions || prev.favorites !== favorites
                        ? { auctions, favorites }
                        : prev
                );
            } catch (e) {
                // ignore errors, keep last known badges
                // console.error("Badges fetch error", e);
            }
        };

        if (authState.isAuthenticated) {
            fetchBadges();
            intervalId = setInterval(fetchBadges, 10000);
        } else {
            setBadges({ auctions: 0, favorites: 0 });
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [authState.isAuthenticated]);

    const login = async (token, _user, _remember_me) => {
        localStorage.setItem("token", token);
        try {
            const data = await api.checkSession(); // this should return { user }
            setAuthState({ isAuthenticated: true, user: data.user });

            if (referer && referer !== "/") {
                navigate(referer);
            }
        } catch (error) {
            console.error("Failed to fetch user after login", error);
            logout();
        }
    };

    const logout = () => {
        localStorage.removeItem("token");
        setAuthState({ isAuthenticated: false, user: null });
    setBadges({ auctions: 0, favorites: 0 });
        navigate("/");
    };

    const setUser = (user) => {
        setAuthState((prev) => ({
            ...prev,
            user,
        }));
    };

    const hasRole = (userRoles, requiredRole) => {
        const allRoles = new Set(userRoles);
        userRoles.forEach((role) => {
            if (ROLE_HIERARCHY[role]) {
                ROLE_HIERARCHY[role].forEach((subRole) => allRoles.add(subRole));
            }
        });
        return allRoles.has(requiredRole);
    };

    return (
        <AuthContext.Provider
            value={{
                ...authState,
                login,
                logout,
                setUser, // ✅ exposed
                setAuthState, // ✅ exposed (optional)
                setReferer,
                hasRole,
                loading,
                badges,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
